import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { ConversationListItem } from "./conversation-list-item";

interface ConversationAccordionSectionProps {
  title: string;
  count: number;
  channels: GroupChannel[];
  isExpanded: boolean;
  onToggle: () => void;
  currentChannelUrl: string | null;
  setCurrentChannelUrl: (url: string) => void;
  shouldEverShowCheckinBadge?: boolean;
  loading?: boolean;
  isSupervisorSection?: boolean;
  onSupervisorChannelSelect?: (url: string) => void;
  unreadCount?: number;
}

export function ConversationAccordionSection({
  title,
  count,
  channels,
  isExpanded,
  onToggle,
  currentChannelUrl,
  setCurrentChannelUrl,
  shouldEverShowCheckinBadge = false,
  loading = false,
  isSupervisorSection = false,
  onSupervisorChannelSelect,
  unreadCount = 0,
}: ConversationAccordionSectionProps) {
  // Check if this is "Needs response" section with 0 count (completed)
  const isCompleted = title === "Needs response" && count === 0;
  const hasUnreadMessages = unreadCount > 0;
  const isSupervisor = title === "Supervisor";

  return (
    <div className="border-b border-zinc-200">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
          isCompleted ? "opacity-60" : ""
        } ${
          hasUnreadMessages && !isCompleted
            ? isSupervisor
              ? "bg-purple-50"
              : "bg-blue-50"
            : ""
        }`}
      >
        <div className="flex items-center gap-2">
          {isCompleted ? (
            <CheckIcon className="h-4 w-4 text-green-600" />
          ) : isExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
          )}
          <span
            className={`text-sm font-medium ${
              isCompleted
                ? "text-gray-500"
                : hasUnreadMessages
                  ? isSupervisor
                    ? "font-semibold text-purple-700"
                    : "font-semibold text-blue-700"
                  : "text-gray-700"
            }`}
          >
            {title}
          </span>
          {hasUnreadMessages && !isCompleted && (
            <div
              className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium text-white ${
                isSupervisor ? "bg-purple-500" : "bg-blue-500"
              }`}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}
        </div>
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
            isCompleted
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {count}
        </span>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t border-zinc-100">
          {loading ? (
            <div className="px-4 py-4">
              <div className="animate-pulse space-y-2">
                <div className="h-12 rounded bg-gray-200"></div>
                <div className="h-12 rounded bg-gray-200"></div>
                <div className="h-12 rounded bg-gray-200"></div>
              </div>
            </div>
          ) : channels.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No conversations</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {channels.map((channel) => (
                <ConversationListItem
                  key={channel.url}
                  channel={channel}
                  currentChannelUrl={currentChannelUrl}
                  onSelect={
                    isSupervisorSection && onSupervisorChannelSelect
                      ? onSupervisorChannelSelect
                      : setCurrentChannelUrl
                  }
                  shouldEverShowCheckinBadge={shouldEverShowCheckinBadge}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
